import { ArrowRight, ArrowSquareOut, BellSlash } from '@phosphor-icons/react';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import Skeleton from '@/components/ui/skeleton';
import Tooltip from '@/components/ui/tooltip';
import type { AnimatedIconHandle } from '@/components/icons/animated-icon';
import { Bell as AnimatedBell } from '@/components/icons/bell';
import { isExternalNotificationLink } from '@/lib/notificationLinks';
import type { AppDispatch, RootState } from '@/store/store';
import {
  fetchNotifications,
  fetchUnreadCount,
  markAllNotificationsRead,
} from '@/slices/notifications/notificationsSlice';

interface NotificationsBellProps {
  /**
   * `default`: the ghost icon button of the header rows and the bare desktop.
   * `window-control`: the small round chrome of the OS window header, matching
   * the maximize and close buttons it sits beside.
   */
  variant?: 'default' | 'window-control';
}

const NotificationsBell = ({ variant = 'default' }: NotificationsBellProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unreadAtOpen, setUnreadAtOpen] = useState<Set<string>>(new Set());
  const openGeneration = useRef(0);
  const bellRef = useRef<AnimatedIconHandle>(null);
  const lastRungCount = useRef(0);

  const { unreadCount, items, listStatus } = useSelector(
    (state: RootState) => state.notifications
  );

  useEffect(() => {
    const refreshCount = () => void dispatch(fetchUnreadCount());

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') refreshCount();
    };

    refreshCount();
    window.addEventListener('focus', refreshCount);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('focus', refreshCount);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [dispatch]);

  // Ring the bell when unread notifications first load, or when more arrive.
  // Dropping back to zero re-arms it so the next arrival rings again.
  useEffect(() => {
    if (unreadCount > lastRungCount.current) {
      lastRungCount.current = unreadCount;
      bellRef.current?.play();
      const timer = window.setTimeout(() => bellRef.current?.stop(), 1200);
      return () => window.clearTimeout(timer);
    }
    if (unreadCount === 0) lastRungCount.current = 0;
    return undefined;
  }, [unreadCount]);

  const handleOpenChange = (nextOpen: boolean) => {
    const generation = ++openGeneration.current;
    setOpen(nextOpen);
    if (!nextOpen) return;

    setUnreadAtOpen(new Set());

    void dispatch(fetchNotifications())
      .unwrap()
      .then((notifications) => {
        if (generation !== openGeneration.current) return;

        const unreadIds = notifications
          .filter((notification) => notification.readAt === null)
          .map((notification) => notification.id);

        setUnreadAtOpen(new Set(unreadIds));
        if (unreadCount > 0 || unreadIds.length > 0) {
          void dispatch(markAllNotificationsRead());
        }
      })
      .catch(() => {});
  };

  const label =
    unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications';

  const badge = unreadCount > 9 ? '9+' : unreadCount;
  // Keep the panel's height stable while the list loads: a refetch (loading
  // or failed) keeps the cached list on screen, and a cold open shows a
  // placeholder shaped like what will most likely replace it (the "caught up"
  // state when nothing is unread, otherwise one row per unread notification,
  // capped at three).
  const isColdLoad = listStatus === 'loading' && items.length === 0;
  const showList = listStatus === 'succeeded' || items.length > 0;
  const skeletonRows = Math.min(unreadCount, 3);
  const isWindowControl = variant === 'window-control';

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <Tooltip title={label}>
        <span>
          <PopoverTrigger asChild>
            {isWindowControl ? (
              <button
                aria-label={label}
                className="os-window__notifications"
                type="button"
              >
                <AnimatedBell
                  ref={bellRef}
                  trigger="none"
                  aria-hidden="true"
                  size={14}
                />
                {unreadCount > 0 && (
                  <span className="absolute -right-1 -top-1 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-status-danger px-0.5 text-xxs leading-none text-white">
                    {badge}
                  </span>
                )}
              </button>
            ) : (
              <Button
                variant="ghost"
                size="icon-lg"
                aria-label={label}
                className="relative"
              >
                <AnimatedBell ref={bellRef} trigger="none" aria-hidden="true" />
                {unreadCount > 0 && (
                  <span className="absolute right-0 top-0 min-w-4 rounded-full bg-status-danger px-1 text-center text-xs leading-4 text-white">
                    {badge}
                  </span>
                )}
              </Button>
            )}
          </PopoverTrigger>
        </span>
      </Tooltip>
      <PopoverContent
        align="end"
        sideOffset={8}
        tone="dense"
        className="w-[360px] max-w-[calc(100vw-2rem)] overflow-hidden"
      >
        {/* PopoverContent is a dark glass surface in both themes (modal
            context), so everything inside uses the neutral-25 ink it sets
            rather than the page-level fg tokens. */}
        <div className="-m-4">
          <div className="border-b border-neutral-0-10 px-4 py-3 font-heading text-sm font-medium">
            Notifications
          </div>
          <div className="max-h-[70vh] overflow-y-auto">
            {isColdLoad && skeletonRows === 0 && (
              <div
                className="flex flex-col items-center gap-2 px-4 py-10"
                aria-hidden="true"
              >
                <Skeleton variant="circular" className="size-5" />
                <Skeleton className="h-4 w-40" />
              </div>
            )}
            {isColdLoad &&
              Array.from({ length: skeletonRows }).map((_, index) => (
                <div
                  key={index}
                  className="space-y-2 border-b border-neutral-0-10 p-4"
                >
                  <Skeleton className="h-4 w-2/3" />
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="h-3 w-1/4" />
                </div>
              ))}
            {listStatus === 'failed' && (
              <div className="p-4 font-body text-sm text-neutral-25/75">
                We couldn’t load notifications.{' '}
                <Button
                  variant="link"
                  context="modal"
                  className="h-auto"
                  onClick={() => handleOpenChange(true)}
                >
                  Retry
                </Button>
              </div>
            )}
            {listStatus === 'succeeded' && items.length === 0 && (
              <div className="flex flex-col items-center gap-2 px-4 py-10 text-center font-body text-sm text-neutral-25/75">
                <BellSlash className="size-5" />
                You&apos;re all caught up
              </div>
            )}
            {showList &&
              items.map((notification) => {
                const isExternal =
                  !!notification.link &&
                  isExternalNotificationLink(notification.link);

                const contents = (
                  <>
                    {unreadAtOpen.has(notification.id) && (
                      <span className="!mt-1.5 size-1.5 shrink-0 rounded-full bg-status-success" />
                    )}
                    {/* Global Sass gives <p> its own margins, so these are forced. */}
                    <div className="min-w-0 flex-1">
                      <p className="!m-0 break-words font-heading text-sm font-medium leading-snug">
                        {notification.title}
                      </p>
                      {notification.body && (
                        <p className="!mb-0 !mt-1 line-clamp-3 break-words font-body text-xs leading-snug text-neutral-25/75">
                          {notification.body}
                        </p>
                      )}
                      {notification.link && (
                        <span className="!mt-2 inline-flex items-center gap-1 font-body text-sm font-medium text-neutral-25">
                          View
                          {isExternal ? (
                            <ArrowSquareOut className="size-3.5" />
                          ) : (
                            <ArrowRight className="size-3.5" />
                          )}
                        </span>
                      )}
                      <p className="!mb-0 !mt-1.5 font-body text-xxs text-neutral-25/60">
                        {moment(notification.createdAt).fromNow()}
                      </p>
                    </div>
                  </>
                );

                const rowClassName =
                  'flex w-full gap-2 border-b border-neutral-0-10 bg-transparent !px-4 !py-3 text-left text-inherit transition-colors hover:bg-neutral-0-10';

                if (isExternal) {
                  return (
                    <a
                      key={notification.id}
                      href={notification.link as string}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={cn(rowClassName, 'no-underline')}
                      onClick={() => setOpen(false)}
                    >
                      {contents}
                    </a>
                  );
                }

                return notification.link ? (
                  <button
                    key={notification.id}
                    className={rowClassName}
                    onClick={() => {
                      navigate(notification.link as string);
                      setOpen(false);
                    }}
                  >
                    {contents}
                  </button>
                ) : (
                  <div
                    key={notification.id}
                    className="flex gap-2 border-b border-neutral-0-10 px-4 py-3"
                  >
                    {contents}
                  </div>
                );
              })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationsBell;
